import React, { memo, useMemo, useState, useCallback, useEffect } from 'react';
import moment from 'moment-timezone';
import { Text, View, StyleSheet } from 'react-native';
import { THEME, COLOR } from '../../../constants/styles';
import { Navigation, Container, Select, FormFooter, FormInput, Utils } from '../../components';

import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { acceptOrRejectEmployerApptRequest, resetStatus } from '../../employer/actions';
import { getCurrentEmployerTimezone } from '../../../selectors/employers';

import { SUCCESS } from '../../../actions/statusTypes';
import {
  getRejectReasonOptions,
  isOtherRejectReasonOption,
  OTHER_REASON_MAX_LENGTH,
} from './reject_reason_options';

const AppointmentRejectReason = memo((props) => {
  useEffect(() => {
    props.actions.resetStatus();
    return () => {
      props.actions.resetStatus();
    };
  }, [props.actions]);
  const options = useMemo(() => getRejectReasonOptions(props.eventType), [props.eventType]);
  const [reason, setReason] = useState(() => options[0] || null);
  const [otherText, setOtherText] = useState('');

  useEffect(() => {
    setReason(
      (current) => options.find((option) => option.id === current?.id) || options[0] || null
    );
  }, [options]);

  const doReject = useCallback(
    (reasonVal, isOneOffReason) => {
      const status = false;
      props.actions.acceptOrRejectEmployerApptRequest(
        props.request.employer_id,
        props.request.appointmentRequestId,
        status,
        reasonVal,
        isOneOffReason
      );
    },
    [props.actions, props.request]
  );
  const handleCancel = useCallback(() => {
    Navigation.dismissAllModals();
  }, []);
  const sanitized = useMemo(
    () => Utils.validate.sanitizeFreeText(otherText).substring(0, OTHER_REASON_MAX_LENGTH),
    [otherText]
  );
  const datetime = useMemo(
    () =>
      props.request?.appointment_time
        ? moment(props.request.appointment_time).tz(props.timezone)
        : null,
    [props.request, props.timezone]
  );
  const appointmentType = useMemo(
    () => props.request?.eventType || props.eventType || 'appointment',
    [props.request, props.eventType]
  );
  const handleSave = useCallback(() => {
    const isOtherRejectReasonOptionFlag = isOtherRejectReasonOption(reason?.id);
    doReject(
      isOtherRejectReasonOptionFlag ? sanitized || null : reason?.id || null,
      isOtherRejectReasonOptionFlag
    );
  }, [doReject, reason, sanitized]);

  const footer = (
    <FormFooter
      onCancel={handleCancel}
      cancelLabel={props.phaRequestStatus === SUCCESS ? 'Done' : 'Cancel'}
      saveLabel={'Yes, Reject'}
      onSave={props.phaRequestStatus === SUCCESS ? null : handleSave}
      saveDisabled={false}
      forceHorizontal={true}
    />
  );
  return (
    <Container
      style={THEME.container}
      errorMessage={props.aptsRequestUpdateError?.message}
      successMessage={
        props.phaRequestStatus === SUCCESS ? 'Appointment request rejected successfully.' : null
      }
      scrollView={true}
      stickyFooter={footer}
    >
      <View style={styles.contents}>
        <Select
          closeOnSelect={true}
          options={options}
          label={'Reason'}
          value={reason}
          onValue={setReason}
          webMode={true}
          required={true}
          menuPlacement="bottom"
        />
        {isOtherRejectReasonOption(reason?.id) && (
          <FormInput
            value={otherText}
            label="Other Reason"
            required={false}
            sanitizer={Utils.validate.sanitizeFreeText}
            onChangeText={setOtherText}
            controlled={true}
            autoCorrect={false}
            maxLength={OTHER_REASON_MAX_LENGTH}
          />
        )}
        {isOtherRejectReasonOption(reason?.id) && otherText.length > 0 && datetime && (
          <>
            <Text style={THEME.labelText}>MESSAGE PREVIEW</Text>
            <View style={styles.sanitizedPreview}>
              <Text style={THEME.textRegular}>
                Your {datetime.format('MMMM D, YYYY')} @ {datetime.format('HH:mm')}{' '}
                {appointmentType} is unavailable due to{' '}
                <Text style={THEME.textBold}>{sanitized}</Text>. Please re-visit the Outpatient App
                scheduling website to try again once addressed.
              </Text>
            </View>
          </>
        )}
        {isOtherRejectReasonOption(reason?.id) ? (
          <Text style={THEME.textRegular}>
            A text and email will be sent to the service member with your comment (not required) and
            will ask them to try again. No URLs will be accepted in the rejection reason that gets
            sent.
          </Text>
        ) : (
          <Text style={THEME.textRegular}>
            A text and email will be sent to the service member with your comment and will ask them
            to try again
          </Text>
        )}
      </View>
    </Container>
  );
});

AppointmentRejectReason.options = {
  width: 400,
};

function mapStateToProps(state, ownProps) {
  return {
    aptsRequestUpdateError: state.screens.employer.phaRequestErrorMessage,
    phaRequestStatus: state.screens.employer.phaRequestStatus,
    timezone: getCurrentEmployerTimezone(state),
  };
}
function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        acceptOrRejectEmployerApptRequest,
        resetStatus,
      },
      dispatch
    ),
  };
}
export default connect(mapStateToProps, mapDispatchToProps)(AppointmentRejectReason);

const styles = StyleSheet.create({
  contents: {
    padding: 35,
    zIndex: 100,
  },
  sanitizedPreview: {
    marginTop: 8,
    marginBottom: 12,
    padding: 10,
    backgroundColor: COLOR.altContrast,
    borderRadius: 4,
  },
});
